/**
 * EF-CREW-ONBOARDING-PRODUCTION-UX-V1: Crew Profile Tabs Component
 * EF-CREW-EDIT-UI-REFINEMENT-AND-VITALE-OCR-V1: UI Refinement
 * 
 * Fixed-size modal window (1200px x 85vh) with 7 tabs:
 * 1. Core Profile - Identity and operational fields
 * 2. Employment - Social security, payment, comments
 * 3. Documents - Document management with sharing permissions
 * 4. Suitability - Roles, qualifications, languages, experience
 * 5. Compliance - Readiness status, missing/expired documents
 * 6. Availability - Future scheduling placeholder
 * 7. Activity - Document uploads, verification history
 * 
 * Each tab has internal scrolling. Window size remains static.
 * 
 * UI Refinement:
 * - Modal width: 1200px (was 1400px)
 * - Two-column layout (60% / 40%)
 * - Active toggle below photo
 * - Max field widths for better proportions
 * - Mobile responsive (<900px single column)
 */

import { useState, useEffect, useCallback, useRef } from "react";
// EVENTSFLO-EMAIL-SAFETY-PREVIEW-RECIPIENT-REDIRECT-AND-AUDIT-V1
import { confirmRealRecipientSend } from "../../lib/realEmailGuard";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
// EVENTSFLO-PROFILE-PHOTO-SIGNED-URL-V1 — render via signed token.
import { SignedStaffPhoto } from "../../lib/SignedStaffPhoto";
import { invalidateSignedPhotoUrl } from "../../lib/photoSignedUrl";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { CountrySelect } from "../shared/CountrySelect";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Shield,
  FileText,
  Award,
  Calendar,
  Clock,
  Activity,
  Camera,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Building,
  CreditCard,
  Users,
  Globe,
  Save,
  X,
  Power,
  Send,
  RefreshCw,
  FileWarning,
  ImageIcon,
  KeyRound,  // EF-ADMIN-SEND-PASSWORD-RESET-FROM-CREW-PROFILE-V1
  BookUser,  // EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
} from "lucide-react";

// EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Import email editor and photo crop modals
import { CrewEmailEditorModal } from "../crew/CrewEmailEditorModal";
import { ProfilePhotoCropModal } from "../crew/ProfilePhotoCropModal";
// EVENTSFLO-ADD-CREW-PHOTO-UPLOAD-AND-CROP-V1: lightweight crop step for direct file uploads
import PhotoCropDialog from "./PhotoCropDialog";
// EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Onboarding help panel
import { CrewOnboardingHelpPanel, OnboardingButtonHelp } from "../guidance/CrewOnboardingHelpPanel";

import { CrewDocumentManager } from "../crew/CrewDocumentManager";
import { StaffSuitabilityTabs } from "./StaffSuitabilityTabs";
// EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
import { PassportIdTab } from "./PassportIdTab";
// EF-CREW-ADMIN-UI-REGRESSION-AND-FETCH-FIX-V1: Use api module for proper error handling
import api, { createStaff, updateStaff, extractErrorMessage } from "../../lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Field width constants for consistent sizing
const FIELD_WIDTH = {
  standard: "max-w-[480px]",
  short: "max-w-[240px]",
  textarea: "max-w-[600px]",
};

// Helper to format date for input
const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  return dateStr.split("T")[0];
};

// Initial form data structure
const getInitialFormData = () => ({
  // Core Profile (Tab 1)
  profile_photo_url: "",
  last_name: "",
  first_name: "",
  gender: "",
  date_of_birth: "",
  // EVENTSFLO-CREW-PLACE-OF-BIRTH-MISSING-IN-ACTIVE-CREW-FORM-FIX-V1
  place_of_birth: "",
  phone: "",        // Mobile
  phone_home: "",   // Home/landline
  email: "",
  address: {
    line1: "",
    line2: "",
    line3: "",
    city: "",
    state: "",
    country: "France",
    postcode: "",
  },
  status: "active",
  employee_id: "",
  employment_type: "casual",
  crew_teams: [],
  assigned_location: "",
  
  // Employment (Tab 2)
  national_insurance_number: "",  // Social Security
  payment_currency: "EUR",
  ignore_conflicts: false,
  notes: "",

  // EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
  // Admin-set Crew Portal UI language. "" maps to null on save (the
  // backend `update_staff` clear-to-null guard for this key wipes the
  // field). Allowed real values: "en", "fr", "ru".
  preferred_portal_language: "",
  
  // Legacy fields for compatibility
  hourly_rate_default: "",
  right_to_work_status: "not_checked",
  right_to_work_expiry: "",
  right_to_work_doc_type: "",
  preference_value: 0,
  access_controls: {
    can_login: false,
    pin_code: "",
    app_notifications_enabled: true,
  },
  emergency_contact: {
    name: "",
    phone: "",
    relationship: "",
  },
  bank_details: {
    account_name: "",
    sort_code: "",
    account_number: "",
  },
  operational_prefs: {
    preferred_venue_ids: [],
    max_hours_per_week: "",
    travel_radius_km: "",
    has_own_transport: false,
    uniform_size: "",
    dietary_requirements: "",
  },
});

// Readiness status badge component
function ReadinessStatusBadge({ status }) {
  const statusConfig = {
    ready: { label: "Ready", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
    missing_documents: { label: "Missing Docs", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: AlertTriangle },
    document_expiring: { label: "Doc Expiring", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: Clock },
    document_expired: { label: "Doc Expired", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
    inactive: { label: "Inactive", color: "bg-slate-500/20 text-slate-400 border-slate-500/30", icon: XCircle },
    // EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Invited status
    invited: { label: "Invited", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Send },
    pending_verification: { label: "Pending Verification", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Clock },
  };
  
  const config = statusConfig[status] || statusConfig.missing_documents;
  const Icon = config.icon;
  
  return (
    <Badge className={cn("text-xs", config.color)}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}

// EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Onboarding status badge
function OnboardingStatusBadge({ status }) {
  const statusConfig = {
    invited: { label: "INVITED", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    pending_profile: { label: "PENDING PROFILE", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
    pending_verification: { label: "PENDING VERIFICATION", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
    active: { label: "ACTIVE", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  };
  
  const config = statusConfig[status];
  if (!config) return null;
  
  return (
    <Badge className={cn("text-[10px] font-semibold tracking-wider", config.color)}>
      {config.label}
    </Badge>
  );
}

// ==================== TAB 1: Core Profile (COMPACT Enterprise Layout) ====================
// EF-UI-STABILIZATION-AND-REGRESSION-FIX-V1: Rebuilt for compact, no-scroll desktop view
// EF-CREW-ADMIN-UI-REGRESSION-AND-FETCH-FIX-V1: Tightened spacing, fixed button label
// EF-CREW-ACCESS-LINK-UNIFIED-V1: Simplified to single access link button
// EF-CREW-PROFILE-PHOTO-AND-EMPLOYMENT-TAB-COMPACT-V1: Added photo upload functionality
function CoreProfileTab({ 
  formData, 
  updateField, 
  updateNestedField, 
  isEditing,
  staff,
  onSendOnboarding,
  sendingOnboarding,
  onPhotoUploaded,
  liveOnboardingStatus,
}) {
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  // EVENTSFLO-ADD-CREW-PHOTO-UPLOAD-AND-CROP-V1: crop step state
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropFileName, setCropFileName] = useState("");

  // EVENTSFLO-CREW-PROFILE-PHOTO-FROM-UPLOADED-ID-DOCUMENT-V1
  // Admin-controlled fallback: pick an already-uploaded identity document
  // (passport / ID front / ID back / driver licence) and crop the face/photo
  // region. Original document is never altered.
  const ID_PHOTO_ELIGIBLE_CODES = ["passport", "national_id_front", "national_id_back", "driver_license"];
  const [idDocPickerOpen, setIdDocPickerOpen] = useState(false);
  const [idDocsLoading, setIdDocsLoading] = useState(false);
  const [idDocsList, setIdDocsList] = useState([]);
  const [docCropOpen, setDocCropOpen] = useState(false);
  const [docCropImageUrl, setDocCropImageUrl] = useState("");
  const [docCropAssetId, setDocCropAssetId] = useState("");
  const [docCropSourceName, setDocCropSourceName] = useState("");
  
  // Check if we can send onboarding
  const canSendOnboarding = isEditing && 
    formData.first_name?.trim() && 
    formData.last_name?.trim() && 
    formData.email?.trim();
  
  const emailMissing = !formData.email?.trim();
  
  // EF-CREW-PROFILE-PHOTO-AND-EMPLOYMENT-TAB-COMPACT-V1: Handle photo upload
  // EVENTSFLO-ADD-CREW-PHOTO-UPLOAD-AND-CROP-V1: open crop dialog instead of uploading raw file
  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, or WebP.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    if (!staff?.id) {
      toast.error('Please save the profile first before uploading a photo.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Read file as data URL for the cropper, then open the crop dialog
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropFileName(file.name);
      setCropOpen(true);
    };
    reader.onerror = () => {
      toast.error('Failed to read image file.');
    };
    reader.readAsDataURL(file);
    // Reset the file input so re-selecting the same file still triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // EVENTSFLO-ADD-CREW-PHOTO-UPLOAD-AND-CROP-V1: upload the cropped result via existing endpoint
  const handleCroppedPhotoSave = async (croppedFile) => {
    if (!staff?.id || !croppedFile) return;
    setUploadingPhoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', croppedFile);
      
      const response = await fetch(`${API_URL}/api/staff/${staff.id}/photo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('eventsflo_auth_token')}`,
        },
        body: formDataUpload,
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to upload photo');
      }
      
      const data = await response.json();
      
      // Update local form data with new photo URL (add timestamp to bust cache)
      const photoUrl = `${API_URL}${data.profile_photo_url}?t=${Date.now()}`;
      updateField('profile_photo_url', photoUrl);
      // EVENTSFLO-PROFILE-PHOTO-SIGNED-URL-V1 — drop the cached signed
      // URL so the next render mints a fresh one pointing to the new
      // bytes on disk.
      if (staff?.id) invalidateSignedPhotoUrl(staff.id);
      
      // Notify parent to refresh staff data
      if (onPhotoUploaded) {
        onPhotoUploaded(photoUrl);
      }
      
      toast.success('Profile photo uploaded successfully');
      setCropOpen(false);
      setCropImageSrc(null);
      setCropFileName("");
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };
  
  // Build photo URL with API base
  const getPhotoUrl = () => {
    if (!formData.profile_photo_url) return null;
    // If it already starts with http, return as-is
    if (formData.profile_photo_url.startsWith('http')) {
      return formData.profile_photo_url;
    }
    // If it's a relative API path, prepend API_URL
    if (formData.profile_photo_url.startsWith('/api/')) {
      return `${API_URL}${formData.profile_photo_url}`;
    }
    return formData.profile_photo_url;
  };

  // EVENTSFLO-CREW-PROFILE-PHOTO-FROM-UPLOADED-ID-DOCUMENT-V1
  // Open the ID-document picker. Fetches current documents and document
  // types in parallel, then keeps only ID-eligible canonical codes.
  const openIdDocPicker = async () => {
    if (!staff?.id) {
      toast.error("Save the crew member first to use uploaded documents.");
      return;
    }
    setIdDocPickerOpen(true);
    setIdDocsLoading(true);
    try {
      const [typesRes, docsRes] = await Promise.all([
        api.get("/document-types"),
        api.get(`/staff/${staff.id}/documents/with-history`),
      ]);
      const types = typesRes.data || [];
      const eligibleTypeIds = new Set(
        types.filter((t) => ID_PHOTO_ELIGIBLE_CODES.includes(t.code)).map((t) => t.id),
      );
      const current = docsRes.data?.current_documents || [];
      const eligible = current
        .filter((d) => eligibleTypeIds.has(d.document_type_id))
        .map((d) => {
          const t = types.find((x) => x.id === d.document_type_id);
          return { ...d, _type_code: t?.code, _type_name: t?.name };
        });
      setIdDocsList(eligible);
    } catch (err) {
      console.error("Failed to load documents for photo pick:", err);
      toast.error("Failed to load uploaded documents");
    } finally {
      setIdDocsLoading(false);
    }
  };

  // EVENTSFLO-CREW-PROFILE-PHOTO-FROM-UPLOADED-ID-DOCUMENT-V1
  // Image documents → open the crop modal targeted at the crew_documents
  // extract endpoint. PDFs are explicitly rejected with the safe message
  // (no risky in-app PDF page rendering).
  const handleSelectIdDoc = (doc) => {
    const mime = (doc.mime_type || "").toLowerCase();
    const filename = doc.display_filename || doc.original_filename || "document";
    if (mime === "application/pdf" || filename.toLowerCase().endsWith(".pdf")) {
      toast.info(
        "PDF cropping is not yet supported. Download/open the document and upload a cropped image.",
        { duration: 6000 },
      );
      return;
    }
    if (!mime.startsWith("image/")) {
      toast.error("This document is not an image. Choose a JPEG / PNG / HEIC ID document.");
      return;
    }
    // Build the authenticated download URL so the crop modal can fetch the
    // image bytes via its existing axios-with-token pipeline.
    const url = `${API_URL}/api/staff/${staff.id}/documents/${doc.id}/download`;
    setDocCropImageUrl(url);
    setDocCropAssetId(doc.id);
    setDocCropSourceName(filename);
    setIdDocPickerOpen(false);
    setDocCropOpen(true);
  };

  // After ProfilePhotoCropModal saves the cropped photo, refresh the visible
  // <img> by repointing profile_photo_url + invalidating the signed-URL
  // cache so the next render mints a fresh signed URL for the new bytes.
  // EVENTSFLO-PROFILE-PHOTO-SIGNED-URL-V1.
  const handleDocPhotoSaved = () => {
    const next = `${API_URL}/api/staff/${staff.id}/photo?t=${Date.now()}`;
    updateField("profile_photo_url", next);
    if (staff?.id) invalidateSignedPhotoUrl(staff.id);
    if (onPhotoUploaded) onPhotoUploaded(next);
    setDocCropOpen(false);
    setDocCropImageUrl("");
    setDocCropAssetId("");
    setDocCropSourceName("");
    toast.success("Profile photo updated from uploaded ID document.");
  };

  const photoUrl = getPhotoUrl();
  
  return (
    <div className="grid grid-cols-12 gap-3">
      {/* Left Column: Photo + Active + Send Invitation (col-span-2) */}
      <div className="col-span-12 lg:col-span-2 flex flex-col items-center gap-1.5">
        {/* EF-CREW-PROFILE-PHOTO-AND-EMPLOYMENT-TAB-COMPACT-V1: Photo with upload overlay
            EVENTSFLO-CREW-PROFILE-PHOTO-ENTRY-POINT-DISCOVERABILITY-V1:
              The avatar is clickable while editing, the overlay caption is
              promoted from `text-[8px]` to `text-xs`, and two clearly visible
              action buttons (Upload photo / From ID) replace the tiny links
              below — so the existing wiring is actually discoverable. */}
        <div className="w-20 lg:w-full max-w-[100px]">
          <div 
            className="aspect-square bg-surface-base/50 rounded border border-dashed border-white/10 flex items-center justify-center overflow-hidden relative group cursor-pointer"
            role={isEditing && staff?.id ? "button" : undefined}
            tabIndex={isEditing && staff?.id ? 0 : undefined}
            onClick={() => isEditing && staff?.id && fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (isEditing && staff?.id && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            data-testid="profile-photo-container"
            aria-label={photoUrl ? "Change profile photo (upload from file)" : "Upload profile photo"}
          >
            {(photoUrl || staff?.id) ? (
              <SignedStaffPhoto
                staffId={staff?.id}
                profilePhotoUrl={photoUrl}
                alt="Profile"
                className="w-full h-full object-cover"
                fallback={<Camera className="h-6 w-6 text-muted-foreground opacity-50" />}
                refreshKey={formData.profile_photo_url}
                dataTestId="profile-photo-image"
              />
            ) : (
              <Camera className="h-6 w-6 text-muted-foreground opacity-50" />
            )}
            
            {/* Upload overlay - only show when editing an existing staff */}
            {isEditing && staff?.id && (
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                {uploadingPhoto ? (
                  <Loader2 className="h-5 w-5 text-white animate-spin" />
                ) : (
                  <div className="text-center">
                    <Upload className="h-4 w-4 text-white mx-auto" />
                    <span className="text-xs text-white block mt-0.5 font-medium">
                      {photoUrl ? 'Change photo' : 'Upload photo'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handlePhotoUpload}
            data-testid="profile-photo-input"
          />
          
          {/* Photo status + action buttons */}
          <div className="mt-1 text-center space-y-1">
            {photoUrl ? (
              <span className="text-[10px] text-emerald-400 block">Photo OK</span>
            ) : isEditing && staff?.id ? null : (
              <span className="text-[10px] text-amber-400 block">No photo</span>
            )}
            {isEditing && staff?.id && (
              <div className="flex flex-col gap-1">
                {/* EVENTSFLO-CREW-PROFILE-PHOTO-ENTRY-POINT-DISCOVERABILITY-V1
                    Both entry points are now visible by default (text-xs).
                    Upload photo: triggers the existing file picker → PhotoCropDialog.
                    From ID:      opens the existing profile-photo-id-picker. */}
                <button
                  type="button"
                  className="w-full text-xs px-2 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 hover:border-blue-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="profile-photo-upload-link"
                  title="Upload a photo file from your computer"
                >
                  {photoUrl ? "Change photo" : "Upload photo"}
                </button>
                <button
                  type="button"
                  className="w-full text-xs px-2 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 hover:border-blue-400 transition-colors"
                  onClick={openIdDocPicker}
                  disabled={uploadingPhoto}
                  data-testid="profile-photo-from-id-link"
                  title="Crop the photo from an uploaded passport / ID / driver licence"
                >
                  From ID
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Active Toggle - compact */}
        <div className="p-1.5 bg-surface-base/30 rounded border border-white/5 w-full max-w-[100px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Power className={cn(
                "h-2.5 w-2.5",
                formData.status === "active" ? "text-emerald-400" : "text-muted-foreground"
              )} />
              <Label className="text-[10px]">Active</Label>
            </div>
            <Switch
              checked={formData.status === "active"}
              onCheckedChange={(checked) => updateField("status", checked ? "active" : "inactive")}
              data-testid="crew-active-switch"
              className="scale-[0.65]"
            />
          </div>
        </div>
        
        {/* EF-CREW-ACCESS-LINK-UNIFIED-V1: Send Access Link Button (replaces separate onboarding/password reset) */}
        {isEditing && (
          <div className="w-full max-w-[100px]">
            {emailMissing ? (
              <div className="p-1 bg-amber-500/10 border border-amber-500/20 rounded text-[7px] text-amber-300 text-center leading-tight">
                Crew email required before sending access link.
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-[8px] h-6 px-1 border-blue-500/30 text-blue-400 hover:bg-blue-500/10 leading-tight"
                onClick={onSendOnboarding}
                disabled={sendingOnboarding || !canSendOnboarding}
                data-testid="send-access-link-btn"
              >
                {sendingOnboarding ? (
                  <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" />
                ) : (
                  <Send className="h-2.5 w-2.5 mr-0.5" />
                )}
                Send Access Link
              </Button>
            )}
            {liveOnboardingStatus && (
              <div className="mt-1 flex justify-center">
                <OnboardingStatusBadge status={liveOnboardingStatus} />
              </div>
            )}
            {staff?.last_access_link_sent_at && (
              <div className="mt-1 text-[7px] text-muted-foreground text-center">
                Last sent: {new Date(staff.last_access_link_sent_at).toLocaleDateString()}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Right Area: Form Fields (col-span-10) - 3-column grid with tighter spacing */}
      <div className="col-span-12 lg:col-span-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1">
          
          {/* Row 1: Name Fields */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">
              LAST NAME <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.last_name}
              onChange={(e) => updateField("last_name", e.target.value.toUpperCase())}
              placeholder="DUPONT"
              className="h-7 uppercase font-semibold text-xs"
              data-testid="crew-last-name"
            />
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">
              First Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.first_name}
              onChange={(e) => updateField("first_name", e.target.value)}
              placeholder="Jean"
              className="h-7 text-xs"
              data-testid="crew-first-name"
            />
          </div>
          <div className="hidden lg:block" />
          
          {/* Row 2: Contact - Email, Telephone, Mobile */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => updateField("email", e.target.value)}
              placeholder="jean.dupont@email.com"
              className="h-7 text-xs"
              data-testid="crew-email"
            />
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Telephone</Label>
            <Input
              value={formData.phone_home}
              onChange={(e) => updateField("phone_home", e.target.value)}
              placeholder="+33 1 23 45 67 89"
              className="h-7 text-xs"
            />
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">
              Mobile <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              placeholder="+33 6 12 34 56 78"
              className="h-7 text-xs"
              data-testid="crew-mobile"
            />
          </div>
          
          {/* Row 3: Gender, DOB */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Gender</Label>
            <Select
              value={formData.gender}
              onValueChange={(val) => updateField("gender", val)}
            >
              <SelectTrigger className="h-7 text-xs" data-testid="crew-gender">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Date of Birth</Label>
            <Input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => updateField("date_of_birth", e.target.value)}
              className="h-7 text-xs"
              data-testid="crew-dob"
            />
          </div>
          {/* EVENTSFLO-CREW-PLACE-OF-BIRTH-MISSING-IN-ACTIVE-CREW-FORM-FIX-V1
              Free-text Place of Birth used by the Crew ID Pack PDF. Clear by
              emptying the input — empty string is sent as null on save so the
              backend `update_staff` honour-explicit-null guard wipes the field. */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Place of Birth</Label>
            <Input
              value={formData.place_of_birth}
              onChange={(e) => updateField("place_of_birth", e.target.value)}
              placeholder="e.g. Nice, Tbilisi, Moscou"
              maxLength={200}
              className="h-7 text-xs"
              data-testid="crew-place-of-birth"
            />
          </div>

          {/* EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
              Admin-settable Crew Portal UI language. "" maps to null
              on save so the existing clear-to-null guard in
              `update_staff` wipes the field. Sits next to Place of
              Birth — both are profile-level preferences the admin can
              pre-set before the crew member logs in. */}
          <div className="md:col-span-2" data-testid="preferred-portal-language-field">
            <Label className="text-[9px] font-medium text-muted-foreground flex items-center gap-1">
              <Globe className="h-3 w-3" />
              Preferred portal language
            </Label>
            <Select
              value={formData.preferred_portal_language || "__none__"}
              onValueChange={(v) =>
                updateField(
                  "preferred_portal_language",
                  v === "__none__" ? "" : v,
                )
              }
            >
              <SelectTrigger
                className="h-7 text-xs"
                data-testid="preferred-portal-language-trigger"
              >
                <SelectValue placeholder="Use crew choice / browser default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value="__none__"
                  data-testid="preferred-portal-language-option-none"
                >
                  Use crew choice / browser default
                </SelectItem>
                <SelectItem
                  value="en"
                  data-testid="preferred-portal-language-option-en"
                >
                  en — English
                </SelectItem>
                <SelectItem
                  value="fr"
                  data-testid="preferred-portal-language-option-fr"
                >
                  fr — French
                </SelectItem>
                <SelectItem
                  value="ru"
                  data-testid="preferred-portal-language-option-ru"
                >
                  ru — Russian
                </SelectItem>
              </SelectContent>
            </Select>
            <p
              className="text-[9px] text-muted-foreground mt-1 leading-snug"
              data-testid="preferred-portal-language-help"
            >
              This controls the Crew Portal interface language across
              devices. It does not change the crew member&apos;s spoken
              languages.
            </p>
          </div>
          
          {/* Row 4: Address Line 1, Line 2, Line 3 */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Address 1</Label>
            <Input
              value={formData.address.line1}
              onChange={(e) => updateNestedField("address", "line1", e.target.value)}
              placeholder="123 Rue de la Paix"
              className="h-7 text-xs"
            />
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Address 2</Label>
            <Input
              value={formData.address.line2}
              onChange={(e) => updateNestedField("address", "line2", e.target.value)}
              placeholder="Apt 4B"
              className="h-7 text-xs"
            />
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Address 3</Label>
            <Input
              value={formData.address.line3 || ""}
              onChange={(e) => updateNestedField("address", "line3", e.target.value)}
              placeholder="Building"
              className="h-7 text-xs"
            />
          </div>
          
          {/* Row 5: City, State, Country/Postcode */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">City</Label>
            <Input
              value={formData.address.city}
              onChange={(e) => updateNestedField("address", "city", e.target.value)}
              placeholder="Paris"
              className="h-7 text-xs"
            />
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">State</Label>
            <Input
              value={formData.address.state || ""}
              onChange={(e) => updateNestedField("address", "state", e.target.value)}
              placeholder="Île-de-France"
              className="h-7 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <Label className="text-[9px] font-medium text-muted-foreground">Country</Label>
              <CountrySelect
                value={formData.address.country}
                onChange={(val) => updateNestedField("address", "country", val)}
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label className="text-[9px] font-medium text-muted-foreground">Postcode</Label>
              <Input
                value={formData.address.postcode}
                onChange={(e) => updateNestedField("address", "postcode", e.target.value)}
                placeholder="75001"
                className="h-7 text-xs"
              />
            </div>
          </div>
          
          {/* Row 6: Operational - Employee No, Employment Type, Crew Teams */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Employee No</Label>
            <Input
              value={formData.employee_id}
              onChange={(e) => updateField("employee_id", e.target.value)}
              placeholder="EMP-001"
              className="h-7 text-xs"
            />
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Employment Type</Label>
            <Select
              value={formData.employment_type}
              onValueChange={(val) => updateField("employment_type", val)}
            >
              <SelectTrigger className="h-7 text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="part_time">Part-time</SelectItem>
                <SelectItem value="full_time">Full-time</SelectItem>
                <SelectItem value="contractor">Contractor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Crew Teams</Label>
            <Input
              value={formData.crew_teams || ""}
              onChange={(e) => updateField("crew_teams", e.target.value)}
              placeholder="Team A, Team B"
              className="h-7 text-xs"
            />
          </div>
          
          {/* Row 7: Assigned Location, Ignore Conflicts */}
          <div>
            <Label className="text-[9px] font-medium text-muted-foreground">Assigned Location</Label>
            <Input
              value={formData.assigned_location || ""}
              onChange={(e) => updateField("assigned_location", e.target.value)}
              placeholder="Paris Office"
              className="h-7 text-xs"
            />
          </div>
          <div className="flex items-end pb-0.5">
            <div className="flex items-center gap-1.5">
              <Switch
                checked={formData.ignore_conflicts || false}
                onCheckedChange={(checked) => updateField("ignore_conflicts", checked)}
                className="scale-[0.65]"
              />
              <Label className="text-[9px] font-medium text-muted-foreground">Ignore Conflicts</Label>
            </div>
          </div>
          <div className="hidden lg:block" />
          
          {/* Row 8: Comments - full width */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3">
            <Label className="text-[9px] font-medium text-muted-foreground">Comments</Label>
            <Textarea
              value={formData.notes || ""}
              onChange={(e) => updateField("notes", e.target.value)}
              placeholder="Internal notes about this crew member..."
              className="h-12 text-xs resize-none"
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* EVENTSFLO-ADD-CREW-PHOTO-UPLOAD-AND-CROP-V1: crop step before saving uploaded photo */}
      <PhotoCropDialog
        open={cropOpen}
        imageSrc={cropImageSrc}
        fileName={cropFileName}
        onCancel={() => {
          setCropOpen(false);
          setCropImageSrc(null);
          setCropFileName("");
        }}
        onConfirm={handleCroppedPhotoSave}
      />

      {/* EVENTSFLO-CREW-PROFILE-PHOTO-FROM-UPLOADED-ID-DOCUMENT-V1
          ID-document picker — admin selects an already-uploaded ID, then the
          existing ProfilePhotoCropModal handles the crop + extract pipeline. */}
      <Dialog
        open={idDocPickerOpen}
        onOpenChange={(o) => {
          if (!o) setIdDocPickerOpen(false);
        }}
      >
        <DialogContent className="max-w-md" data-testid="profile-photo-id-picker">
          <DialogHeader>
            <DialogTitle>Use uploaded ID document</DialogTitle>
            <DialogDescription>
              Pick an uploaded passport / ID / driver licence and manually crop the photo area.
              The source document is never altered. This is not automatic face detection.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-1.5">
            {idDocsLoading ? (
              <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading documents…
              </div>
            ) : idDocsList.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">
                No eligible ID documents uploaded yet. Upload a passport / ID / driver
                licence in the Documents tab first.
              </div>
            ) : (
              idDocsList.map((d) => {
                const mime = (d.mime_type || "").toLowerCase();
                const isPdf = mime === "application/pdf" || (d.original_filename || "").toLowerCase().endsWith(".pdf");
                const isImage = mime.startsWith("image/");
                const label = d.display_filename || d.original_filename || "Document";
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => handleSelectIdDoc(d)}
                    disabled={!isImage}
                    className={cn(
                      "w-full text-left p-2 rounded border flex items-center gap-2 transition-colors",
                      isImage
                        ? "border-white/10 hover:bg-surface-elevated/50 cursor-pointer"
                        : "border-white/5 opacity-60 cursor-not-allowed",
                    )}
                    data-testid={`profile-photo-id-doc-${d.id}`}
                  >
                    <ImageIcon className={cn("h-4 w-4 shrink-0", isImage ? "text-blue-400" : "text-muted-foreground")} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{label}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {d._type_name || d._type_code} · {(d.mime_type || "image").split("/").pop()?.toUpperCase()}
                        {isPdf ? " · PDF cropping not supported" : ""}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-CREW-PROFILE-PHOTO-FROM-UPLOADED-ID-DOCUMENT-V1
          The actual crop modal — reuses the existing ProfilePhotoCropModal
          which POSTs to /api/crew-documents/{id}/extract-profile-photo with
          the crop region. The source document is never modified. */}
      {/* EVENTSFLO-CREW-PROFILE-FROM-ID-DOC-SELECTION-DOES-NOTHING-FIX-V1:
          ProfilePhotoCropModal's contract is `open` / `onOpenChange` (matches
          radix Dialog), NOT `isOpen` / `onClose`. With the wrong prop names
          the modal would never mount even when state was set — selecting a
          document silently did nothing. `sourceType` / `sourceName` were
          also dropped because the modal doesn't accept them. */}
      {docCropOpen && (
        <ProfilePhotoCropModal
          open={docCropOpen}
          onOpenChange={(o) => {
            if (!o) {
              setDocCropOpen(false);
              setDocCropImageUrl("");
              setDocCropAssetId("");
              setDocCropSourceName("");
            }
          }}
          imageUrl={docCropImageUrl}
          assetId={docCropAssetId}
          staffId={staff?.id}
          onSaveComplete={handleDocPhotoSaved}
        />
      )}
    </div>
  );
}

// ==================== TAB 2: Employment (COMPACT Layout) ====================
// EF-CREW-PROFILE-PHOTO-AND-EMPLOYMENT-TAB-COMPACT-V1: Redesigned for compact, no-scroll desktop view
function EmploymentTab({ formData, updateField, updateNestedField }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-[1000px]">
      {/* LEFT COLUMN: Employment Info & Bank Details */}
      <div className="space-y-3">
        {/* Section 1: Employment Info */}
        <div className="p-3 bg-surface-base/20 rounded-lg border border-white/5">
          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <CreditCard className="h-3 w-3" />
            Employment Info
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[9px] text-muted-foreground">ID Number</Label>
              <Input
                value={formData.national_insurance_number}
                onChange={(e) => updateField("national_insurance_number", e.target.value)}
                placeholder="National ID / NI / SSN"
                className="h-7 text-xs font-mono"
                data-testid="crew-id-number"
              />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground">Currency</Label>
              <Select
                value={formData.payment_currency || "EUR"}
                onValueChange={(val) => updateField("payment_currency", val)}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CHF">CHF</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Section 2: Bank Details */}
        <div className="p-3 bg-surface-base/20 rounded-lg border border-white/5">
          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Building className="h-3 w-3" />
            Bank Details
          </h4>
          <div className="space-y-2">
            <div>
              <Label className="text-[9px] text-muted-foreground">Account Name</Label>
              <Input
                value={formData.bank_details?.account_name || ""}
                onChange={(e) => updateNestedField("bank_details", "account_name", e.target.value)}
                placeholder="Jean Dupont"
                className="h-7 text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[9px] text-muted-foreground">Sort Code / BIC</Label>
                <Input
                  value={formData.bank_details?.sort_code || ""}
                  onChange={(e) => updateNestedField("bank_details", "sort_code", e.target.value)}
                  placeholder="BNPAFRPP"
                  className="h-7 text-xs font-mono"
                />
              </div>
              <div>
                <Label className="text-[9px] text-muted-foreground">Account No. / IBAN</Label>
                <Input
                  value={formData.bank_details?.account_number || ""}
                  onChange={(e) => updateNestedField("bank_details", "account_number", e.target.value)}
                  placeholder="FR76..."
                  className="h-7 text-xs font-mono"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Section 3: Comments - inline */}
        <div className="p-3 bg-surface-base/20 rounded-lg border border-white/5">
          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <FileText className="h-3 w-3" />
            Notes
          </h4>
          <Textarea
            value={formData.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            placeholder="Internal notes..."
            rows={2}
            className="text-xs resize-none h-16"
          />
        </div>
      </div>
      
      {/* RIGHT COLUMN: Emergency Contact & Scheduling */}
      <div className="space-y-3">
        {/* Section 4: Emergency Contact */}
        <div className="p-3 bg-amber-500/5 rounded-lg border border-amber-500/20">
          <h4 className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3" />
            Emergency Contact
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[9px] text-muted-foreground">Name</Label>
              <Input
                value={formData.emergency_contact?.name || ""}
                onChange={(e) => updateNestedField("emergency_contact", "name", e.target.value)}
                placeholder="Marie Dupont"
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground">Phone</Label>
              <Input
                value={formData.emergency_contact?.phone || ""}
                onChange={(e) => updateNestedField("emergency_contact", "phone", e.target.value)}
                placeholder="+33 6 98 76 54 32"
                className="h-7 text-xs"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[9px] text-muted-foreground">Relationship</Label>
              <Input
                value={formData.emergency_contact?.relationship || ""}
                onChange={(e) => updateNestedField("emergency_contact", "relationship", e.target.value)}
                placeholder="Spouse"
                className="h-7 text-xs"
              />
            </div>
          </div>
        </div>
        
        {/* Section 5: Scheduling Preferences */}
        <div className="p-3 bg-surface-base/20 rounded-lg border border-white/5">
          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            Scheduling
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 p-1.5 bg-surface-base/30 rounded border border-white/5">
              <Switch
                checked={formData.ignore_conflicts || false}
                onCheckedChange={(checked) => updateField("ignore_conflicts", checked)}
                className="scale-[0.7]"
              />
              <Label className="text-[9px]">Ignore Conflicts</Label>
            </div>
            <div className="flex items-center gap-2 p-1.5 bg-surface-base/30 rounded border border-white/5">
              <Switch
                checked={formData.operational_prefs?.has_own_transport || false}
                onCheckedChange={(checked) => updateNestedField("operational_prefs", "has_own_transport", checked)}
                className="scale-[0.7]"
              />
              <Label className="text-[9px]">Own Transport</Label>
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground">Max Hrs/Week</Label>
              <Input
                type="number"
                value={formData.operational_prefs?.max_hours_per_week || ""}
                onChange={(e) => updateNestedField("operational_prefs", "max_hours_per_week", e.target.value)}
                placeholder="48"
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground">Travel Radius (km)</Label>
              <Input
                type="number"
                value={formData.operational_prefs?.travel_radius_km || ""}
                onChange={(e) => updateNestedField("operational_prefs", "travel_radius_km", e.target.value)}
                placeholder="50"
                className="h-7 text-xs"
              />
            </div>
          </div>
        </div>
        
        {/* Section 6: Additional Preferences */}
        <div className="p-3 bg-surface-base/20 rounded-lg border border-white/5">
          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Users className="h-3 w-3" />
            Preferences
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[9px] text-muted-foreground">Uniform Size</Label>
              <Select
                value={formData.operational_prefs?.uniform_size || ""}
                onValueChange={(val) => updateNestedField("operational_prefs", "uniform_size", val)}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="XS">XS</SelectItem>
                  <SelectItem value="S">S</SelectItem>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="L">L</SelectItem>
                  <SelectItem value="XL">XL</SelectItem>
                  <SelectItem value="XXL">XXL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground">Dietary Requirements</Label>
              <Input
                value={formData.operational_prefs?.dietary_requirements || ""}
                onChange={(e) => updateNestedField("operational_prefs", "dietary_requirements", e.target.value)}
                placeholder="None"
                className="h-7 text-xs"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== TAB 5: Compliance ====================
function ComplianceTab({ 
  staff, 
  formData,
  updateField,
  onRequestProfileUpdate,
  onRequestMissingDocuments,
  sendingRequest,
  identityVerified,  // EVENTSFLO-FIX-IDENTITY-VERIFIED-FRONTEND-REFRESH-V1: derived from fresh GET /api/staff/{id}
}) {
  const readinessStatus = staff?.readiness_status || formData?.readiness_status || "missing_documents";
  const hasMissingDocs = staff?.missing_documents?.length > 0;
  const hasExpiredDocs = staff?.expired_documents?.length > 0;
  const hasEmail = staff?.email || formData?.email;
  
  return (
    <div className="space-y-6">
      {/* EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Onboarding Help Panel */}
      <CrewOnboardingHelpPanel />
      
      {/* EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Compliance Actions */}
      <div className="flex items-center gap-3 p-3 bg-surface-elevated/30 rounded-lg border border-white/5">
        <div className="flex-1">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Compliance Actions
          </h4>
          <p className="text-xs text-muted-foreground">Send requests to crew member</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRequestProfileUpdate}
            disabled={sendingRequest || !hasEmail}
            data-testid="request-profile-update-btn"
          >
            {sendingRequest ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Request Profile Update
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRequestMissingDocuments}
            disabled={sendingRequest || !hasEmail || !hasMissingDocs}
            className={hasMissingDocs ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10" : ""}
            data-testid="request-missing-docs-btn"
          >
            {sendingRequest ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileWarning className="h-3.5 w-3.5 mr-1.5" />}
            Request Missing Documents
          </Button>
        </div>
      </div>
      
      {/* Readiness Status */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Shield className="h-3.5 w-3.5" />
          Shift Readiness
        </h4>
        <div className="bg-surface-base/30 rounded-lg p-4 border border-white/5">
          <div className="flex items-center gap-4">
            <ReadinessStatusBadge status={readinessStatus} />
            {staff?.readiness_reason && (
              <span className="text-sm text-muted-foreground">{staff.readiness_reason}</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Missing Documents */}
      <div className="border-t border-white/10 pt-4">
        <h4 className="text-xs font-medium text-amber-500 uppercase tracking-wider mb-3 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" />
          Missing Documents
        </h4>
        <div className="bg-surface-base/30 rounded-lg p-4 border border-white/5">
          {staff?.missing_documents?.length > 0 ? (
            <ul className="space-y-2">
              {staff.missing_documents.map((doc, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <XCircle className="h-4 w-4 text-amber-500" />
                  {doc}
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              All required documents uploaded
            </div>
          )}
        </div>
      </div>
      
      {/* Expired Documents */}
      <div className="border-t border-white/10 pt-4">
        <h4 className="text-xs font-medium text-red-500 uppercase tracking-wider mb-3 flex items-center gap-2">
          <XCircle className="h-3.5 w-3.5" />
          Expired Documents
        </h4>
        <div className="bg-surface-base/30 rounded-lg p-4 border border-white/5">
          {staff?.expired_documents?.length > 0 ? (
            <ul className="space-y-2">
              {staff.expired_documents.map((doc, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-red-500" />
                  {doc.name} - Expired {doc.expiry_date}
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              No expired documents
            </div>
          )}
        </div>
      </div>
      
      {/* Compliance Flags */}
      <div className="border-t border-white/10 pt-4">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Shield className="h-3.5 w-3.5" />
          Compliance Flags
        </h4>
        <div className="bg-surface-base/30 rounded-lg p-4 border border-white/5 space-y-4">
          {/* EVENTSFLO-ADD-RIGHT-TO-WORK-EDITOR-TO-CREW-PROFILE-V1: editable RTW block */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Right to Work</span>
              <Badge className={cn(
                "text-xs",
                (formData?.right_to_work_status || staff?.right_to_work_status) === "verified"
                  ? "bg-emerald-500/20 text-emerald-400"
                  : (formData?.right_to_work_status || staff?.right_to_work_status) === "expired"
                    ? "bg-red-500/20 text-red-400"
                    : (formData?.right_to_work_status || staff?.right_to_work_status) === "pending"
                      ? "bg-amber-500/20 text-amber-400"
                      : "bg-muted/40 text-muted-foreground"
              )}>
                {(formData?.right_to_work_status || staff?.right_to_work_status || "not_checked").replace("_", " ")}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Status</Label>
                <Select
                  value={formData?.right_to_work_status || "not_checked"}
                  onValueChange={(v) => updateField?.("right_to_work_status", v)}
                  disabled={!updateField}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="rtw-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_checked">Not Checked</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Expiry</Label>
                <Input
                  type="date"
                  value={formData?.right_to_work_expiry || ""}
                  onChange={(e) => updateField?.("right_to_work_expiry", e.target.value)}
                  className="h-8 text-xs"
                  disabled={!updateField}
                  data-testid="rtw-expiry-input"
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Supporting Document</Label>
                <Input
                  type="text"
                  value={formData?.right_to_work_doc_type || ""}
                  onChange={(e) => updateField?.("right_to_work_doc_type", e.target.value)}
                  placeholder="e.g. passport, work permit"
                  className="h-8 text-xs"
                  disabled={!updateField}
                  data-testid="rtw-doc-type-input"
                />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Manual verification only. Save the profile to persist changes.
            </p>
          </div>

          <div className="flex items-center justify-between border-t border-white/5 pt-3">
            <span className="text-sm">Identity Verified</span>
            {(() => {
              // EVENTSFLO-FIX-IDENTITY-VERIFIED-FRONTEND-REFRESH-V1:
              // Prefer the freshly-fetched derived value from GET /api/staff/{id}
              // (which runs the verified-Passport / verified-NID-Front+Back rule).
              // Fall back to the staff prop only if the live fetch hasn't returned
              // yet (which would be `null`), so we don't briefly flash Pending
              // while the fresh request is in flight.
              const derived = identityVerified !== null && identityVerified !== undefined
                ? identityVerified
                : Boolean(staff?.identity_verified);
              return (
                <Badge className={cn(
                  "text-xs",
                  derived
                    ? "bg-emerald-500/20 text-emerald-400"
                    : "bg-amber-500/20 text-amber-400"
                )} data-testid="identity-verified-badge">
                  {derived ? "Verified" : "Pending"}
                </Badge>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== TAB 6: Availability ====================
function AvailabilityTab({ staff }) {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center text-muted-foreground">
        <Calendar className="h-16 w-16 mx-auto mb-4 opacity-30" />
        <h3 className="text-lg font-medium mb-2">Availability Module</h3>
        <p className="text-sm max-w-md">
          Future scheduling and availability management will be displayed here.
          This feature is planned for a future release.
        </p>
      </div>
    </div>
  );
}

// ==================== TAB 7: Activity ====================
function ActivityTab({ staff }) {
  // Mock activity data - in production this would come from the API
  const activities = staff?.recent_activity || [];
  
  return (
    <div className="space-y-4">
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
        <Activity className="h-3.5 w-3.5" />
        Recent Activity
      </h4>
      
      {activities.length > 0 ? (
        <div className="space-y-3">
          {activities.map((activity, idx) => (
            <div key={idx} className="bg-surface-base/30 rounded-lg p-3 border border-white/5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {activity.type === "document_upload" && <Upload className="h-4 w-4 text-primary" />}
                  {activity.type === "verification" && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                  {activity.type === "profile_update" && <User className="h-4 w-4 text-blue-400" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{activity.description}</p>
                  <p className="text-xs text-muted-foreground">{activity.timestamp}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-surface-base/30 rounded-lg p-8 border border-white/5 text-center text-muted-foreground">
          <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No recent activity recorded</p>
        </div>
      )}
      
      {/* Activity Types Legend */}
      <div className="border-t border-white/10 pt-4">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Activity Types
        </h4>
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <Upload className="h-3.5 w-3.5 text-primary" />
            Document Uploads
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
            Verifications
          </div>
          <div className="flex items-center gap-2">
            <User className="h-3.5 w-3.5 text-blue-400" />
            Profile Updates
          </div>
        </div>
      </div>
    </div>
  );
}


// ==================== MAIN COMPONENT ====================
export function CrewProfileTabs({
  open,
  onOpenChange,
  staff,
  onSuccess,
  venues = [],
  prefillData = null,
}) {
  const [activeTab, setActiveTab] = useState("core-profile");
  const [formData, setFormData] = useState(getInitialFormData());
  const [submitting, setSubmitting] = useState(false);
  
  // EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTemplateType, setEmailTemplateType] = useState(null);
  const [emailVariables, setEmailVariables] = useState({});
  const [sendingOnboarding, setSendingOnboarding] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  
  // EF-ADMIN-SEND-PASSWORD-RESET-FROM-CREW-PROFILE-V1: Password reset state
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);

  // ONB-FIX-031: Live onboarding summary — same source of truth as the Documents
  // tab. Replaces the persisted (drift-prone) staff.readiness_status / staff.onboarding_status
  // for the top-right badges in the dialog header and the right-column profile area.
  const [liveOnboardingSummary, setLiveOnboardingSummary] = useState(null);

  // EVENTSFLO-FIX-IDENTITY-VERIFIED-FRONTEND-REFRESH-V1:
  // The `staff` prop comes from the staff list row (GET /api/staff?limit=...),
  // which does NOT compute the derived `identity_verified` flag. Only
  // GET /api/staff/{id} runs the derivation (verified Passport OR verified
  // National ID Front + Back). Re-fetch the single record on modal open so
  // the Compliance "Identity Verified" badge reflects backend truth.
  // EVENTSFLO-FIX-SUITABILITY-UI-REFRESH-AFTER-CV-UPDATE-V1: also keep the
  // full fresh staff record for the Suitability tab, which was previously
  // rendered with no `staffData` prop and therefore always showed 0/0/0/0.
  const [liveIdentityVerified, setLiveIdentityVerified] = useState(null);
  const [liveStaff, setLiveStaff] = useState(null);
  const [staffRefreshTick, setStaffRefreshTick] = useState(0);

  const refreshLiveStaff = useCallback(() => setStaffRefreshTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!open || !staff?.id) {
      setLiveOnboardingSummary(null);
      setLiveIdentityVerified(null);
      setLiveStaff(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await api.get(`/staff/${staff.id}/onboarding-summary`);
        if (!cancelled) setLiveOnboardingSummary(res.data || null);
      } catch (err) {
        if (!cancelled) {
          console.warn("[ONB-FIX-031] onboarding-summary fetch failed:", err);
          setLiveOnboardingSummary(null);
        }
      }
    })();
    // EVENTSFLO-FIX-IDENTITY-VERIFIED-FRONTEND-REFRESH-V1 +
    // EVENTSFLO-FIX-SUITABILITY-UI-REFRESH-AFTER-CV-UPDATE-V1:
    // fresh single-staff fetch (drives both Identity Verified and Suitability).
    (async () => {
      try {
        const res = await api.get(`/staff/${staff.id}`);
        if (!cancelled) {
          setLiveStaff(res.data || null);
          setLiveIdentityVerified(Boolean(res.data?.identity_verified));
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[live staff] fresh fetch failed:", err);
          setLiveStaff(null);
          setLiveIdentityVerified(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, staff?.id, activeTab, staffRefreshTick]);

  // Map the live summary into the badge enums used by the existing badges.
  const liveReadinessStatus = (() => {
    const s = liveOnboardingSummary;
    if (!s) return staff?.readiness_status || "missing_documents";
    if (s.has_rejected) return "missing_documents";
    if (s.has_expired) return "document_expired";
    if (s.total_required > 0 && s.total_verified < s.total_required) {
      return s.total_pending > 0 ? "missing_documents" : "missing_documents";
    }
    if (s.has_expiring_soon) return "document_expiring";
    if (s.total_required > 0 && s.total_verified >= s.total_required) return "ready";
    return staff?.readiness_status || "missing_documents";
  })();

  const liveOnboardingStatus = (() => {
    const s = liveOnboardingSummary;
    if (!s) return staff?.onboarding_status || null;
    if (s.has_rejected) return "rejected";
    if (s.total_required > 0 && s.total_verified >= s.total_required) return "active";
    if (s.total_verified > 0 || s.total_pending > 0) return "pending_verification";
    return staff?.onboarding_status || "pending_profile";
  })();
  
  const isEditing = !!staff?.id;
  
  // Reset tab when dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab("core-profile");
    }
  }, [open]);
  
  // Populate form with staff data or prefill data
  useEffect(() => {
    if (staff) {
      setFormData({
        ...getInitialFormData(),
        profile_photo_url: staff.profile_photo_url || "",
        last_name: staff.last_name || "",
        first_name: staff.first_name || "",
        gender: staff.gender || "",
        date_of_birth: formatDateForInput(staff.date_of_birth) || "",
        // EVENTSFLO-CREW-PLACE-OF-BIRTH-MISSING-IN-ACTIVE-CREW-FORM-FIX-V1
        place_of_birth: staff.place_of_birth || "",
        phone: staff.phone || "",
        phone_home: staff.phone_home || "",
        email: staff.email || "",
        address: {
          line1: staff.address?.line1 || "",
          line2: staff.address?.line2 || "",
          line3: staff.address?.line3 || "",
          city: staff.address?.city || "",
          state: staff.address?.state || "",
          country: staff.address?.country || "France",
          postcode: staff.address?.postcode || "",
        },
        status: staff.status || "active",
        employee_id: staff.employee_id || "",
        employment_type: staff.employment_type || "casual",
        crew_teams: staff.crew_teams || [],
        assigned_location: staff.assigned_location || "",
        national_insurance_number: staff.national_insurance_number || "",
        payment_currency: staff.payment_currency || "EUR",
        ignore_conflicts: staff.ignore_conflicts || false,
        // EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
        preferred_portal_language: staff.preferred_portal_language || "",
        notes: staff.notes || "",
        hourly_rate_default: staff.hourly_rate_default || "",
        // EVENTSFLO-ADD-RIGHT-TO-WORK-EDITOR-TO-CREW-PROFILE-V1: hydrate RTW fields from staff
        right_to_work_status: staff.right_to_work_status || "not_checked",
        right_to_work_expiry: formatDateForInput(staff.right_to_work_expiry) || "",
        right_to_work_doc_type: staff.right_to_work_doc_type || "",
        preference_value: staff.preference_value || 0,
        access_controls: staff.access_controls || getInitialFormData().access_controls,
        emergency_contact: staff.emergency_contact || getInitialFormData().emergency_contact,
        bank_details: staff.bank_details || getInitialFormData().bank_details,
        operational_prefs: staff.operational_prefs || getInitialFormData().operational_prefs,
      });
    } else if (prefillData) {
      // Apply prefill data from OCR
      setFormData({
        ...getInitialFormData(),
        last_name: prefillData.last_name || "",
        first_name: prefillData.first_name || "",
        email: prefillData.email || "",
        phone: prefillData.phone || "",
        date_of_birth: formatDateForInput(prefillData.date_of_birth) || "",
        national_insurance_number: prefillData.vitale_number || prefillData.vitale_number_normalized || "",
        address: prefillData.address ? {
          ...getInitialFormData().address,
          ...prefillData.address,
        } : getInitialFormData().address,
      });
    } else {
      setFormData(getInitialFormData());
    }
  }, [staff, prefillData]);
  
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);
  
  const updateNestedField = useCallback((parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  }, []);
  
  const handleSubmit = async () => {
    // Validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error("First name and last name are required");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }
    
    setSubmitting(true);
    try {
      // Build full name
      const fullName = `${formData.first_name} ${formData.last_name}`.trim();
      
      // EF-CREW-ADMIN-UI-REGRESSION-AND-FETCH-FIX-V1: Convert empty strings to null for optional fields
      // Backend Pydantic models expect null for optional date/enum fields, not empty strings
      // Also handle nested objects properly
      const cleanOperationalPrefs = formData.operational_prefs ? {
        ...formData.operational_prefs,
        max_hours_per_week: formData.operational_prefs.max_hours_per_week || null,
        travel_radius_km: formData.operational_prefs.travel_radius_km || null,
      } : null;
      
      const payload = {
        ...formData,
        full_name: fullName,
        // Convert empty strings to null for fields that backend expects as null or valid values
        date_of_birth: formData.date_of_birth || null,
        // EVENTSFLO-CREW-PLACE-OF-BIRTH-MISSING-IN-ACTIVE-CREW-FORM-FIX-V1:
        // free-text city/town. Empty after trim → null so the backend
        // `update_staff` clear-to-null special case actually wipes the field.
        place_of_birth: (formData.place_of_birth || "").trim() || null,
        // EVENTSFLO-CREW-PREFERRED-LANGUAGE-ADMIN-EDIT-V1
        // Always send so an admin can clear the value. "" → null so the
        // backend `update_staff` clear-to-null guard wipes the field.
        preferred_portal_language: formData.preferred_portal_language || null,
        gender: formData.gender || null,
        profile_photo_url: formData.profile_photo_url || null,
        phone_home: formData.phone_home || null,
        employee_id: formData.employee_id || null,
        national_insurance_number: formData.national_insurance_number || null,
        notes: formData.notes || null,
        assigned_location: formData.assigned_location || null,
        right_to_work_expiry: formData.right_to_work_expiry || null,
        // EVENTSFLO-ADD-RIGHT-TO-WORK-EDITOR-TO-CREW-PROFILE-V1: persist RTW status + doc type
        right_to_work_status: formData.right_to_work_status || "not_checked",
        right_to_work_doc_type: formData.right_to_work_doc_type || null,
        hourly_rate_default: formData.hourly_rate_default || null,
        operational_prefs: cleanOperationalPrefs,
      };
      
      // EF-CREW-ADMIN-UI-REGRESSION-AND-FETCH-FIX-V1: Use axios api module instead of raw fetch
      // This properly handles response body parsing and error extraction
      let responseData;
      if (isEditing) {
        const response = await updateStaff(staff.id, payload);
        responseData = response.data;
      } else {
        const response = await createStaff(payload);
        responseData = response.data;
      }
      
      toast.success(isEditing ? "Crew profile updated" : "Crew profile created");
      onSuccess?.(responseData);
      onOpenChange(false);
    } catch (err) {
      console.error("Save error:", err);
      console.error("Error response:", err.response?.data);
      // Use extractErrorMessage for proper Pydantic validation error handling
      const errorMessage = extractErrorMessage(err);
      console.error("Extracted message:", errorMessage);
      toast.error(errorMessage || "Failed to save crew profile");
    } finally {
      setSubmitting(false);
    }
  };
  
  // EF-CREW-ACCESS-LINK-UNIFIED-V1: Send unified access link directly (no modal)
  const handleSendOnboarding = useCallback(async () => {
    if (!staff?.id) {
      toast.error("Crew ID is required");
      return;
    }
    
    if (!staff?.email?.trim()) {
      toast.error("Email is required to send access link");
      return;
    }

    // EVENTSFLO-EMAIL-SAFETY-PREVIEW-RECIPIENT-REDIRECT-AND-AUDIT-V1
    // Typed-CONFIRM gate when the recipient is not obviously a TEST
    // address. The backend safety redirect will still rewrite the
    // outbound recipient in preview — this is defence in depth.
    const _displayName = `${staff.first_name || ""} ${staff.last_name || ""}`.trim()
      || staff.full_name || staff.name || "";
    if (!confirmRealRecipientSend({ name: _displayName, email: staff.email })) {
      toast.info("Access link send cancelled.");
      return;
    }

    setSendingOnboarding(true);
    try {
      const response = await fetch(`${API_URL}/api/staff/${staff.id}/send-access-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("eventsflo_auth_token")}`,
        },
        body: JSON.stringify({
          language: "en",
        }),
      });
      
      const data = await response.json();
      
      // EF-FIX-SEND-ACCESS-LINK-FRONTEND-ERROR-HANDLING-V1: Log response for debugging
      console.log("ACCESS LINK RESPONSE:", data);
      
      // EF-FIX-EMAIL-SEND-ACCESS-LINK-SILENT-FAILURE-V1: Handle explicit error responses
      if (!response.ok) {
        // Extract error details from response
        const errorDetail = data.detail;
        let errorMessage = "Failed to send access link";
        
        if (typeof errorDetail === "object") {
          // Structured error response
          errorMessage = errorDetail.message || errorDetail.error || errorMessage;
        } else if (typeof errorDetail === "string") {
          errorMessage = errorDetail;
        }
        
        throw new Error(errorMessage);
      }
      
      // EF-FIX-SEND-ACCESS-LINK-FRONTEND-ERROR-HANDLING-V1: 
      // Check for explicit error in response body (not just HTTP status)
      if (data.error) {
        throw new Error(data.message || data.error);
      }
      
      // EF-FIX-EMAIL-SEND-ACCESS-LINK-SILENT-FAILURE-V1: Check for dev mode warning
      if (data.warning === "email_not_sent_dev_mode") {
        toast.warning(`Email not sent (dev mode)`, {
          description: `Use link: ${data._dev_access_link}`,
          duration: 10000,
        });
        return;
      }
      
      // EF-FIX-SEND-ACCESS-LINK-FRONTEND-ERROR-HANDLING-V1:
      // Success case: success === true
      if (data.success === true) {
        // Check if email was actually sent
        if (data.email_sent) {
          // EF-FIX-LINK-TYPE-DETECTION-AND-EMAIL-TEMPLATE-SELECTION-V1: Clear distinct messages
          if (data.link_type === "onboarding") {
            toast.success("Onboarding link sent", {
              description: `Sent to ${staff.email} - new crew will set up password`,
            });
          } else {
            toast.success("Access link sent", {
              description: `Sent to ${staff.email} - existing crew login`,
            });
          }
        } else {
          // Success but email not sent (rare case)
          toast.warning("Access link generated but email may not have been sent", {
            description: data.message,
          });
        }
        
        // Refresh staff data to show updated last_access_link_sent_at
        // Note: onSuccess triggers loadData which should NOT fail
        if (onSuccess) {
          onSuccess(staff);
        }
        return;
      }
      
      // Fallback: unexpected response format
      console.warn("Unexpected response format:", data);
      toast.warning("Access link processed", {
        description: data.message || "Check if email was received",
      });
      
    } catch (error) {
      console.error("Send access link error:", error);
      // EF-FIX-SEND-ACCESS-LINK-FRONTEND-ERROR-HANDLING-V1: Show clear error message
      // ONLY show error toast for actual failures, not for data loading issues
      toast.error(error.message || "Failed to send access link", {
        description: "Please check email provider configuration in Platform Settings.",
        duration: 8000,
      });
    } finally {
      setSendingOnboarding(false);
    }
  }, [staff?.id, staff?.email, onSuccess]);
  
  // EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Handle profile update request
  const handleRequestProfileUpdate = useCallback(() => {
    setEmailTemplateType("CREW_PROFILE_UPDATE_REQUEST");
    setEmailVariables({
      company_name: "EventsFlo",
      portal_link: `${window.location.origin}/crew-portal`,
      admin_name: "Admin",
      support_email: "support@eventsflo.ai",
    });
    setEmailModalOpen(true);
  }, []);
  
  // EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Handle missing documents request
  const handleRequestMissingDocuments = useCallback(() => {
    // Format missing documents as HTML list
    const missingDocs = staff?.missing_documents || [];
    const missingDocsHtml = missingDocs.length > 0 
      ? `<ul>${missingDocs.map(doc => `<li>${doc}</li>`).join('')}</ul>`
      : "";
    
    setEmailTemplateType("CREW_MISSING_DOCUMENTS_REQUEST");
    setEmailVariables({
      company_name: "EventsFlo",
      portal_link: `${window.location.origin}/crew-portal`,
      admin_name: "Admin",
      support_email: "support@eventsflo.ai",
      missing_documents: missingDocsHtml,
    });
    setEmailModalOpen(true);
  }, [staff?.missing_documents]);
  
  // EF-ADMIN-SEND-PASSWORD-RESET-FROM-CREW-PROFILE-V1: Send password reset email
  const handleSendPasswordReset = useCallback(async () => {
    if (!formData.email?.trim()) {
      toast.error("Email is required to send password reset");
      return;
    }
    
    setSendingPasswordReset(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";
      const response = await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email.trim() }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Password reset email sent successfully");
      } else {
        toast.error(data?.detail || "Failed to send password reset email");
      }
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error("Failed to send password reset email");
    } finally {
      setSendingPasswordReset(false);
    }
  }, [formData.email]);
  
  // EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Send email via backend
  // EF-ONBOARDING-INVITATION-AND-BUILD-THEME-FIX-V1: Fixed endpoint to use correct staff onboarding route
  const handleSendEmail = useCallback(async (emailData) => {
    if (!staff?.id) {
      throw new Error("Crew ID is required");
    }
    
    const templateType = emailData.template_type;
    let endpoint = "";
    
    if (templateType === "CREW_ONBOARDING_INVITATION") {
      // Use the correct endpoint that generates temp password and required docs
      endpoint = `/api/staff/${staff.id}/send-onboarding-email`;
      setSendingOnboarding(true);
    } else if (templateType === "CREW_PROFILE_UPDATE_REQUEST") {
      endpoint = `/api/staff/${staff.id}/send-profile-update-request`;
      setSendingRequest(true);
    } else if (templateType === "CREW_MISSING_DOCUMENTS_REQUEST") {
      endpoint = `/api/staff/${staff.id}/send-missing-documents-request`;
      setSendingRequest(true);
    } else {
      throw new Error("Unknown template type");
    }
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("eventsflo_auth_token")}`,
        },
        body: JSON.stringify({
          template_id: emailData.template_id,
          language: emailData.language,
          custom_subject: emailData.subject,
          custom_body: emailData.body,
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to send email");
      }
      
      // Refresh staff data to show updated status
      if (onSuccess) {
        onSuccess(staff);
      }
    } finally {
      setSendingOnboarding(false);
      setSendingRequest(false);
    }
  }, [staff?.id, onSuccess]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-card border-white/10 p-0 overflow-hidden flex flex-col [&>button]:hidden"
        style={{ 
          width: "1200px", 
          maxWidth: "95vw", 
          height: "85vh", 
          maxHeight: "85vh" 
        }}
        data-testid="crew-profile-modal"
      >
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-white/10 bg-surface-elevated/30 shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3">
              <User className="h-5 w-5 text-primary" />
              {isEditing ? "Edit Crew Profile" : "New Crew Profile"}
              {staff && (
                <Badge variant="outline" className="ml-2">
                  {staff.full_name || `${staff.first_name} ${staff.last_name}`}
                </Badge>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {staff && <ReadinessStatusBadge status={liveReadinessStatus} />}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-white/10"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        {/* Tabs Content - Fixed height with internal scroll */}
        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="mx-6 mt-4 mb-0 bg-surface-elevated/30 border border-white/5 w-fit shrink-0">
              <TabsTrigger value="core-profile" className="gap-1.5 text-xs" data-testid="tab-core-profile">
                <User className="h-3.5 w-3.5" />
                Core Profile
              </TabsTrigger>
              <TabsTrigger value="employment" className="gap-1.5 text-xs" data-testid="tab-employment">
                <Briefcase className="h-3.5 w-3.5" />
                Employment
              </TabsTrigger>
              <TabsTrigger value="documents" className="gap-1.5 text-xs" data-testid="tab-documents">
                <FileText className="h-3.5 w-3.5" />
                Documents
              </TabsTrigger>
              <TabsTrigger value="suitability" className="gap-1.5 text-xs" data-testid="tab-suitability">
                <Award className="h-3.5 w-3.5" />
                Suitability
              </TabsTrigger>
              <TabsTrigger value="compliance" className="gap-1.5 text-xs" data-testid="tab-compliance">
                <Shield className="h-3.5 w-3.5" />
                Compliance
              </TabsTrigger>
              {/* EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1 */}
              <TabsTrigger value="passport-id" className="gap-1.5 text-xs" data-testid="tab-passport-id">
                <BookUser className="h-3.5 w-3.5" />
                Passport / ID
              </TabsTrigger>
              <TabsTrigger value="availability" className="gap-1.5 text-xs" data-testid="tab-availability">
                <Calendar className="h-3.5 w-3.5" />
                Availability
              </TabsTrigger>
              <TabsTrigger value="activity" className="gap-1.5 text-xs" data-testid="tab-activity">
                <Activity className="h-3.5 w-3.5" />
                Activity
              </TabsTrigger>
            </TabsList>
            
            {/* Tab Contents with internal scroll */}
            <div className="flex-1 overflow-hidden">
              <TabsContent value="core-profile" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  <CoreProfileTab
                    formData={formData}
                    updateField={updateField}
                    updateNestedField={updateNestedField}
                    isEditing={isEditing}
                    staff={staff}
                    onSendOnboarding={handleSendOnboarding}
                    sendingOnboarding={sendingOnboarding}
                    liveOnboardingStatus={liveOnboardingStatus}
                    onPhotoUploaded={(photoUrl) => {
                      // Update form data with new photo URL
                      updateField('profile_photo_url', photoUrl);
                    }}
                  />
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="employment" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  <EmploymentTab
                    formData={formData}
                    updateField={updateField}
                    updateNestedField={updateNestedField}
                  />
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="documents" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  {isEditing && staff?.id ? (
                    <CrewDocumentManager staffId={staff.id} />
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      <div className="text-center">
                        <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Save the crew profile first to manage documents</p>
                      </div>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="suitability" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  {isEditing && staff?.id ? (
                    <StaffSuitabilityTabs
                      staffId={staff.id}
                      staffData={liveStaff || staff}
                      onUpdate={refreshLiveStaff}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      <div className="text-center">
                        <Award className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Save the crew profile first to manage suitability</p>
                      </div>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="compliance" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  <ComplianceTab 
                    staff={staff} 
                    formData={formData}
                    updateField={updateField}
                    onRequestProfileUpdate={handleRequestProfileUpdate}
                    onRequestMissingDocuments={handleRequestMissingDocuments}
                    sendingRequest={sendingRequest}
                    identityVerified={liveIdentityVerified}
                  />
                </ScrollArea>
              </TabsContent>

              {/* EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1 */}
              <TabsContent value="passport-id" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  {isEditing && staff?.id ? (
                    <PassportIdTab staffId={staff.id} />
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      <div className="text-center">
                        <BookUser className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Save the crew profile first to manage Passport / ID details</p>
                      </div>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="availability" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  <AvailabilityTab staff={staff} />
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="activity" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <ScrollArea className="flex-1 px-6 py-4">
                  <ActivityTab staff={staff} />
                </ScrollArea>
              </TabsContent>
            </div>
          </Tabs>
        </div>
        
        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-white/10 bg-surface-elevated/30 shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-muted-foreground">
              {isEditing && staff?.created_at && (
                <span>Created: {new Date(staff.created_at).toLocaleDateString()}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-crew-btn">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEditing ? "Update Profile" : "Create Crew"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
      
      {/* EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Email Editor Modal */}
      <CrewEmailEditorModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        templateType={emailTemplateType}
        crew={{
          first_name: formData.first_name || staff?.first_name,
          last_name: formData.last_name || staff?.last_name,
          email: formData.email || staff?.email,
        }}
        variables={emailVariables}
        onSend={handleSendEmail}
        title={
          emailTemplateType === "CREW_ONBOARDING_INVITATION" 
            ? "Send Onboarding Invitation"
            : emailTemplateType === "CREW_PROFILE_UPDATE_REQUEST"
            ? "Request Profile Update"
            : emailTemplateType === "CREW_MISSING_DOCUMENTS_REQUEST"
            ? "Request Missing Documents"
            : "Send Email"
        }
      />
    </Dialog>
  );
}

export default CrewProfileTabs;
